import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface KlineData {
  date: string;
  value: number;
}

const fetchTrxPriceHistory = async (days: number = 14): Promise<KlineData[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('binance-price', {
      body: { symbol: 'TRXUSDT', interval: '1d', limit: days }
    });

    if (error) {
      console.warn('Binance price history fetch failed, using fallback');
      return generateFallbackData(days);
    }

    if (!Array.isArray(data)) {
      return generateFallbackData(days);
    }

    // Binance klines format: [openTime, open, high, low, close, volume, closeTime, ...]
    return data.map((kline: (string | number)[]) => ({
      date: new Date(kline[0] as number).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: parseFloat(kline[4] as string), // Close price
    }));
  } catch (error) {
    console.warn('Error fetching price history:', error);
    return generateFallbackData(days);
  }
};

const generateFallbackData = (days: number): KlineData[] => {
  const data: KlineData[] = [];
  const basePrice = 0.28;
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: basePrice + (Math.random() - 0.5) * 0.02
    });
  }
  return data;
};

export const useTrxPriceHistory = (days: number = 14) => {
  return useQuery({
    queryKey: ['trx-price-history', days],
    queryFn: () => fetchTrxPriceHistory(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
